import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { PERMISSION } from 'src/features/auth/types';
import { useGetGateway } from 'src/features/gateway/api/useGetGateway';
import { GATEWAY_IDENTIFIER_TYPE_MAP } from 'src/features/gateway/utils/constants';
import { gatewayMessages } from 'src/features/gateway/utils/messages';
import { PATH } from 'src/features/navigation/utils/constants';
import SidePanelAttribute from 'src/features/stay/components/sidePanel/SidePanelAttribute';
import { VISITOR_CATEGORIES_TRANSLATION_MAP } from 'src/features/zone/utils/constants';
import { zoneMessages } from 'src/features/zone/utils/messages';
import { VISITOR_CATEGORY_TYPE } from 'src/features/zone/utils/types';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useParking } from 'src/providers/ParkingProvider';
import { Button } from 'src/registry/ui/Button';
import {
    DialogBody,
    DialogClose,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { generalMessages } from 'src/registry/utils/messages';
import { BOOLEAN_MAP, ICON_MAP } from 'src/utils/constants';

type GatewaySidePanelProps = {
    gatewayId?: string;
};

export const GATEWAY_SIDE_PANEL_NAME = 'gateway-side-panel';

const GatewaySidePanel = ({ gatewayId }: GatewaySidePanelProps) => {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    const navigate = useNavigate();

    const { data: gatewayData } = useGetGateway({
        parkingId,
        gatewayId: gatewayId || '',
        config: { enabled: !!gatewayId },
    });
    const accessZones = useHasPermissions([PERMISSION.ACCESS_ZONES]);

    return (
        <>
            {gatewayData ? (
                <DialogHeader>
                    <div className="flex items-center gap-2">
                        <DialogTitle className="pr-0 font-mono">
                            {gatewayData.translations.find((translation) => translation.language === locale)?.name ??
                                ''}
                        </DialogTitle>
                    </div>
                </DialogHeader>
            ) : (
                <DialogHeader>
                    <Skeleton className="h-7 w-32" />
                    <Skeleton className="h-5 w-60" />
                </DialogHeader>
            )}

            <DialogSeparator />
            <DialogBody>
                <div className="flex aspect-video min-h-44 w-full flex-col items-center justify-center rounded-t-lg bg-gray-100">
                    <FontAwesomeIcon icon={ICON_MAP.BARRIER} size="2xl" className="opacity-50" />
                </div>
                <Button
                    variant="outline"
                    className="cursor-pointer"
                    onClick={() => gatewayId && navigate(`/${PATH.ADMINISTRATION}/gateway/${gatewayId}`)}
                >
                    {formatMessage(gatewayMessages.openGatewayDashboard)}
                </Button>

                <div className="mt-4 grid gap-4 sm:gap-6">
                    {gatewayData?.translations.map((translation) => {
                        const languageToUpper = translation.language.toUpperCase();
                        return (
                            <SidePanelAttribute
                                key={languageToUpper}
                                label={`${formatMessage(generalMessages.name)} (${languageToUpper})`}
                                value={translation.name}
                                className="flex flex-col gap-1"
                            />
                        );
                    })}
                    {gatewayData?.entry_zone ? (
                        gatewayData.entry_zone.translations.map((translation) => {
                            const languageToUpper = translation.language.toUpperCase();
                            return (
                                <SidePanelAttribute
                                    key={languageToUpper}
                                    label={`${formatMessage(gatewayMessages.entryZone)} (${languageToUpper})`}
                                    value={translation.name}
                                    link={
                                        accessZones
                                            ? `/${PATH.ADMINISTRATION}/zone/${gatewayData.entry_zone.id}`
                                            : undefined
                                    }
                                    className="flex flex-col gap-1"
                                />
                            );
                        })
                    ) : (
                        <SidePanelAttribute
                            label={formatMessage(gatewayMessages.entryZone)}
                            value="-"
                            className="flex flex-col gap-1"
                        />
                    )}

                    {gatewayData?.exit_zone ? (
                        gatewayData.exit_zone.translations.map((translation) => {
                            const languageToUpper = translation.language.toUpperCase();
                            return (
                                <SidePanelAttribute
                                    key={languageToUpper}
                                    label={`${formatMessage(gatewayMessages.exitZone)} (${languageToUpper})`}
                                    value={translation.name}
                                    link={
                                        accessZones
                                            ? `/${PATH.ADMINISTRATION}/zone/${gatewayData.exit_zone.id}`
                                            : undefined
                                    }
                                    className="flex flex-col gap-1"
                                />
                            );
                        })
                    ) : (
                        <SidePanelAttribute
                            label={formatMessage(gatewayMessages.exitZone)}
                            value="-"
                            className="flex flex-col gap-1"
                        />
                    )}

                    <SidePanelAttribute
                        label={formatMessage(gatewayMessages.requiredIdentifiers)}
                        value={gatewayData?.required_identifier_types
                            .map((type) => formatMessage(GATEWAY_IDENTIFIER_TYPE_MAP[type]))
                            .join(', ')}
                        className="flex flex-col gap-1"
                    />
                    <SidePanelAttribute
                        label={formatMessage(gatewayMessages.allowUnknownMainIdentifier)}
                        value={
                            gatewayData?.allow_unknown_main_identifier !== undefined
                                ? formatMessage(
                                      BOOLEAN_MAP[
                                          String(gatewayData.allow_unknown_main_identifier) as 'true' | 'false'
                                      ],
                                  )
                                : '-'
                        }
                        className="flex flex-col gap-1"
                    />
                    <SidePanelAttribute
                        label={formatMessage(gatewayMessages.requireAllIdentifier)}
                        value={
                            gatewayData?.require_all_identifier !== undefined
                                ? formatMessage(
                                      BOOLEAN_MAP[String(gatewayData.require_all_identifier) as 'true' | 'false'],
                                  )
                                : '-'
                        }
                        className="flex flex-col gap-1"
                    />
                    <SidePanelAttribute
                        label={formatMessage(zoneMessages.visitorCategories)}
                        value={
                            gatewayData?.visitor_categories?.length
                                ? gatewayData?.visitor_categories
                                      ?.map((g) =>
                                          formatMessage(
                                              VISITOR_CATEGORIES_TRANSLATION_MAP[g as VISITOR_CATEGORY_TYPE] ?? {
                                                  id: 'unknown',
                                                  defaultMessage: g,
                                              },
                                          ),
                                      )
                                      .join(', ')
                                : '-'
                        }
                        className="flex flex-col gap-1"
                    />
                    {gatewayData?.translations.map((translation) => {
                        const languageToUpper = translation.language.toUpperCase();
                        return (
                            <SidePanelAttribute
                                label={`${formatMessage(generalMessages.description)} (${languageToUpper})`}
                                value={translation.description?.length ? translation.description : '-'}
                                key={translation.id}
                                className="flex flex-col gap-1"
                            />
                        );
                    })}
                </div>
            </DialogBody>
            <DialogFooter>
                <DialogClose asChild>
                    <Button variant="outline" className="flex-1">
                        {formatMessage(generalMessages.close)}
                    </Button>
                </DialogClose>
            </DialogFooter>
        </>
    );
};

export default GatewaySidePanel;
